import React, { useState } from 'react';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Rating,
  Card,
  CardMedia,
  IconButton,
  LinearProgress
} from '@mui/material';
import {
  CloudUpload,
  Delete,
  LocationOn,
  CalendarToday,
  Star
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useDropzone } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { getApiUrl } from '../utils/api';

const CreatePost = () => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    locationName: '',
    latitude: '',
    longitude: '',
    country: '',
    city: '',
    startDate: null,
    endDate: null,
    tags: '',
    visibility: 'public',
    tripType: '',
    budget: 'mid-range',
    rating: 5
  });
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);

  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
    }
  }, [isAuthenticated, navigate]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      'image/*': ['.jpeg', '.jpg', '.png', '.gif'],
      'video/*': ['.mp4', '.mov', '.avi', '.webm']
    },
    maxFiles: 10,
    maxSize: 100 * 1024 * 1024, // 100MB
    onDrop: (acceptedFiles) => {
      const newFiles = acceptedFiles.map(file => ({
        file,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        type: file.type.startsWith('video/') ? 'video' : 'image'
      }));
      setFiles(prev => [...prev, ...newFiles]);
    }
  });

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleDateChange = (field) => (date) => {
    setFormData({
      ...formData,
      [field]: date
    });
  };

  const removeFile = (index) => {
    setFiles(prev => {
      const newFiles = [...prev];
      if (newFiles[index].preview) {
        URL.revokeObjectURL(newFiles[index].preview);
      }
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setUploadProgress(0);

    try {
      const formDataToSend = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          if (key === 'startDate' || key === 'endDate') {
            formDataToSend.append(key, formData[key].toISOString());
          } else if (key === 'latitude' || key === 'longitude') {
            // Only add coordinates if they are valid numbers
            const numValue = parseFloat(formData[key]);
            if (!isNaN(numValue)) {
              formDataToSend.append(key, numValue.toString());
            }
          } else {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      // Add media files
      files.forEach(fileObj => {
        formDataToSend.append('media', fileObj.file);
      });

      const response = await axios.post(getApiUrl('/api/posts'), formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      navigate(`/post/${response.data.post._id}`);
    } catch (err) {
      console.error('Error creating post:', err);
      setError(err.response?.data?.message || 'Failed to create post');
    } finally {
      setLoading(false);
      setUploadProgress(0);
    }
  };

  const tripTypes = [
    'solo', 'couple', 'family', 'friends', 'business', 'adventure', 'relaxation'
  ];

  const budgetOptions = [
    'budget', 'mid-range', 'luxury'
  ];

  return (
    <Container maxWidth="md" sx={{ mt: 4, pb: 4 }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Share Your Trip Experience
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Tell the world about your amazing journey
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" gutterBottom>
              Uploading... {uploadProgress}%
            </Typography>
            <LinearProgress variant="determinate" value={uploadProgress} />
          </Box>
        )}

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Title */}
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  name="title"
                  label="Trip Title"
                  value={formData.title}
                  onChange={handleChange}
                  placeholder="e.g., Amazing weekend in Paris"
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  multiline
                  rows={4}
                  name="description"
                  label="Description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Share your experience, what made this trip special..."
                />
              </Grid>

              {/* Location */}
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  name="locationName"
                  label="Location"
                  value={formData.locationName}
                  onChange={handleChange}
                  placeholder="e.g., Eiffel Tower, Paris"
                  InputProps={{
                    startAdornment: <LocationOn sx={{ mr: 1, color: 'text.secondary' }} />
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  name="city"
                  label="City"
                  value={formData.city}
                  onChange={handleChange}
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  name="country"
                  label="Country"
                  value={formData.country}
                  onChange={handleChange}
                />
              </Grid>

              {/* Dates */}
              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="Start Date"
                  value={formData.startDate}
                  onChange={handleDateChange('startDate')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true
                    }
                  }}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DatePicker
                  label="End Date"
                  value={formData.endDate}
                  onChange={handleDateChange('endDate')}
                  minDate={formData.startDate}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      required: true
                    }
                  }}
                />
              </Grid>

              {/* Trip Type */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth required>
                  <InputLabel>Trip Type</InputLabel>
                  <Select
                    name="tripType"
                    value={formData.tripType}
                    onChange={handleChange}
                    label="Trip Type"
                  >
                    {tripTypes.map(type => (
                      <MenuItem key={type} value={type}>
                        {type.charAt(0).toUpperCase() + type.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Budget */}
              <Grid item xs={12} sm={4}>
                <FormControl fullWidth>
                  <InputLabel>Budget</InputLabel>
                  <Select
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    label="Budget"
                  >
                    {budgetOptions.map(budget => (
                      <MenuItem key={budget} value={budget}>
                        {budget.charAt(0).toUpperCase() + budget.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Rating */}
              <Grid item xs={12} sm={4}>
                <Box>
                  <Typography component="legend" gutterBottom>
                    Overall Rating
                  </Typography>
                  <Rating
                    name="rating"
                    value={formData.rating}
                    onChange={(event, newValue) => {
                      setFormData({ ...formData, rating: newValue });
                    }}
                    size="large"
                  />
                </Box>
              </Grid>

              {/* Tags */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  name="tags"
                  label="Tags"
                  value={formData.tags}
                  onChange={handleChange}
                  placeholder="beach, adventure, food, culture (comma separated)"
                  helperText="Add tags to help others discover your post"
                />
              </Grid>

              {/* Media Upload */}
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Photos & Videos
                </Typography>
                <Box
                  {...getRootProps()}
                  sx={{
                    border: '2px dashed',
                    borderColor: isDragActive ? 'primary.main' : 'grey.300',
                    borderRadius: 2,
                    p: 3,
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: isDragActive ? 'action.hover' : 'background.paper'
                  }}
                >
                  <input {...getInputProps()} />
                  <CloudUpload sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    {isDragActive ? 'Drop files here' : 'Drag & drop files here'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    or click to select files (max 10 files, 100MB each)
                  </Typography>
                </Box>

                {/* File Preview */}
                {files.length > 0 && (
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    {files.map((fileObj, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card>
                          {fileObj.type === 'image' ? (
                            <CardMedia
                              component="img"
                              height="140"
                              image={fileObj.preview}
                              alt={`Preview ${index}`}
                            />
                          ) : (
                            <Box
                              sx={{
                                height: 140,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                backgroundColor: 'grey.100'
                              }}
                            >
                              <Typography variant="body2">
                                Video: {fileObj.file.name}
                              </Typography>
                            </Box>
                          )}
                          <Box sx={{ p: 1, display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="caption" noWrap>
                              {fileObj.file.name}
                            </Typography>
                            <IconButton
                              size="small"
                              onClick={() => removeFile(index)}
                              color="error"
                            >
                              <Delete />
                            </IconButton>
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Grid>

              {/* Visibility */}
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Visibility</InputLabel>
                  <Select
                    name="visibility"
                    value={formData.visibility}
                    onChange={handleChange}
                    label="Visibility"
                  >
                    <MenuItem value="public">Public</MenuItem>
                    <MenuItem value="followers">Followers Only</MenuItem>
                    <MenuItem value="private">Private</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ mt: 2, py: 1.5 }}
                >
                  {loading ? 'Sharing Your Trip...' : 'Share Trip'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </LocalizationProvider>
      </Paper>
    </Container>
  );
};

export default CreatePost;
